// =============================================================================
// giftcard — physical gift cards (cadeaubon): code helpers, scan parsing and
// RPC wrappers (migration 20261019130617_booking_gift_cards.sql).
// =============================================================================
// A code is 12 characters from ALPHABET (no I/O/0/1): 11 random plus a check
// character = ALPHABET[sum((2i+1) * index_i) mod 32]. Mirrors the database
// (booking.fn_gift_card_check_char), supabase/functions/_shared/giftcard.ts and
// widget/src/giftcard/logic.ts. Change all four together.
import { supabase } from './supabase';

export const GIFT_CARD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const GIFT_CARD_URL_BASE = 'https://neoseum.com/c/';

export function normalizeGiftCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function giftCheckChar(body: string): string | null {
  if (body.length !== 11) return null;
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    const idx = GIFT_CARD_ALPHABET.indexOf(body.charAt(i));
    if (idx < 0) return null;
    sum += (2 * i + 1) * idx;
  }
  return GIFT_CARD_ALPHABET.charAt(sum % 32);
}

/** True for a NORMALIZED code with the gift card shape and a matching check character. */
export function isGiftCode(code: string): boolean {
  if (!/^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{12}$/.test(code)) return false;
  return giftCheckChar(code.slice(0, 11)) === code.charAt(11);
}

export function formatGiftCode(code: string): string {
  return code.length === 12 ? `${code.slice(0, 4)}-${code.slice(4, 8)}-${code.slice(8)}` : code;
}

export function giftCardUrl(code: string): string {
  return GIFT_CARD_URL_BASE + code;
}

/**
 * The gift card code in a scanned or typed string, or null. Accepts the card's
 * QR (https://neoseum.com/c/<CODE>, with or without www/scheme/trailing slash),
 * the page it redirects to (/cadeaubon?code=<CODE>) and a bare code with or
 * without dashes. The check character keeps booking numbers (EHV-5DEJ3W),
 * Bokun tickets (NEO-T…) and 32-hex ticket tokens out.
 */
export function parseGiftCardScan(raw: string): string | null {
  const text = raw.trim();
  if (text.length === 0 || text.length > 200) return null;

  const url = text.match(/^(?:https?:\/\/)?(?:www\.)?neoseum\.com\/(?:[a-z]{2}\/)?(c\/([^/?#]+)|cadeaubon\/?\?(?:[^#]*&)?code=([^&#]+))/i);
  if (url) {
    const code = normalizeGiftCode(decodeURIComponent(url[2] ?? url[3] ?? ''));
    return isGiftCode(code) ? code : null;
  }

  // A bare code: only letters, digits, dashes and spaces, 12 significant chars.
  if (!/^[A-Za-z0-9\- ]+$/.test(text)) return null;
  const code = normalizeGiftCode(text);
  return isGiftCode(code) ? code : null;
}

export function formatEuro(cents: number): string {
  return `€ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

// -----------------------------------------------------------------------------
// Status
// -----------------------------------------------------------------------------
export type GiftCardDisplayStatus = 'inactive' | 'active' | 'expired' | 'depleted' | 'blocked';

export function giftCardStatusOf(s: GiftCardDisplayStatus): { label: string; className: string } {
  switch (s) {
    case 'inactive': return { label: 'Niet geactiveerd', className: 'voucher-status-warn' };
    case 'active':   return { label: 'Actief',           className: 'voucher-status-ok' };
    case 'expired':  return { label: 'Verlopen',         className: 'voucher-status-off' };
    case 'depleted': return { label: 'Opgebruikt',       className: 'voucher-status-off' };
    case 'blocked':  return { label: 'Geblokkeerd',      className: 'voucher-status-off' };
  }
}

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
export interface GiftCard {
  id: string;
  serial: number;
  code: string;
  code_display: string;
  status: 'inactive' | 'active';
  display_status: GiftCardDisplayStatus;
  balance_cents: number;
  held_cents: number;
  available_cents: number;
  currency: string;
  activated_at: string | null;
  expires_at: string | null;
  last_loaded_at: string | null;
  blocked_at: string | null;
  blocked_reason: string | null;
  created_at: string;
  batch_id: string;
  batch_label: string | null;
}

export type GiftCardLoadStatus = 'pending' | 'succeeded' | 'failed' | 'canceled' | 'refunded';

export interface GiftCardLoad {
  id: string;
  amount_cents: number;
  status: GiftCardLoadStatus;
  is_activation: boolean;
  created_at: string;
  succeeded_at: string | null;
  failure_code: string | null;
  location_name: string | null;
  operator_email: string | null;
  stripe_payment_intent_id: string | null;
}

export type GiftCardTxKind = 'load' | 'redeem' | 'refund_credit' | 'load_reversal' | 'adjust' | 'expire';

export interface GiftCardTransaction {
  id: string;
  kind: GiftCardTxKind;
  amount_cents: number;
  balance_after_cents: number;
  created_at: string;
  booking_id: string | null;
  booking_number: string | null;
  note: string | null;
  operator_email: string | null;
}

export interface GiftCardHold {
  id: string;
  amount_cents: number;
  status: 'active' | 'committed' | 'released';
  channel: 'online' | 'counter';
  created_at: string;
  settled_at: string | null;
  release_reason: string | null;
  booking_id: string;
  booking_number: string | null;
}

export interface GiftCardEvent {
  id: string;
  action: string;
  actor_type: string;
  actor_label: string | null;
  from_value: unknown;
  to_value: unknown;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface GiftCardDetail {
  card: GiftCard;
  loads: GiftCardLoad[];
  transactions: GiftCardTransaction[];
  holds: GiftCardHold[];
  events: GiftCardEvent[];
}

export interface GiftCardSearchRow {
  id: string;
  serial: number;
  code: string;
  code_display: string;
  display_status: GiftCardDisplayStatus;
  balance_cents: number;
  expires_at: string | null;
  batch_label: string;
}

export interface GiftCardBatch {
  id: string;
  label: string;
  card_count: number;
  notes: string | null;
  created_at: string;
  exported_at: string | null;
  export_count: number;
  created_by_email: string | null;
  serial_from: number | null;
  serial_to: number | null;
  active_count: number;
  blocked_count: number;
  balance_cents: number;
}

export interface GiftCardBatchExport {
  batch_id: string;
  label: string;
  export_count: number;
  cards: { serial: number; code: string }[];
}

export const TX_KIND_LABEL: Record<GiftCardTxKind, string> = {
  load:          'Opgewaardeerd',
  redeem:        'Besteed',
  refund_credit: 'Teruggezet (annulering)',
  load_reversal: 'Opwaardering terugbetaald',
  adjust:        'Correctie',
  expire:        'Verlopen',
};

// -----------------------------------------------------------------------------
// RPCs
// -----------------------------------------------------------------------------
export async function fetchGiftCardDetail(code: string): Promise<GiftCardDetail | null> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_gift_card_detail', { p_code: code });
  if (error) {
    if (/gift_card_not_found/.test(error.message)) return null;
    throw error;
  }
  return data as GiftCardDetail;
}

export async function searchGiftCards(query: string): Promise<GiftCardSearchRow[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_search_gift_cards', { p_query: query, p_limit: 25 });
  if (error) throw error;
  return (data as GiftCardSearchRow[]) ?? [];
}

export async function listGiftCardBatches(): Promise<GiftCardBatch[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_list_gift_card_batches');
  if (error) throw error;
  return (data as GiftCardBatch[]) ?? [];
}

export async function createGiftCardBatch(count: number, label: string, notes: string | null) {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_create_gift_card_batch', { p_count: count, p_label: label, p_notes: notes });
  if (error) throw error;
  return data as { batch_id: string; label: string; card_count: number; serial_from: number; serial_to: number };
}

export async function exportGiftCardBatch(batchId: string): Promise<GiftCardBatchExport> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_export_gift_card_batch', { p_batch_id: batchId });
  if (error) throw error;
  return data as GiftCardBatchExport;
}

export async function setGiftCardBlocked(id: string, blocked: boolean, reason: string | null) {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_admin_gift_card_set_blocked', { p_gift_card_id: id, p_blocked: blocked, p_reason: reason });
  if (error) throw error;
}

export async function adjustGiftCard(id: string, deltaCents: number, reason: string) {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_gift_card_adjust', { p_gift_card_id: id, p_delta_cents: deltaCents, p_reason: reason });
  if (error) throw error;
  return data as { balance_cents: number };
}

// -----------------------------------------------------------------------------
// Printer CSV
// -----------------------------------------------------------------------------
export function giftCardBatchCsvRows(exp: GiftCardBatchExport): string[][] {
  return [
    ['serial', 'code', 'code_display', 'url'],
    ...exp.cards.map((c) => [String(c.serial), c.code, formatGiftCode(c.code), giftCardUrl(c.code)]),
  ];
}

// -----------------------------------------------------------------------------
// Counter: spend a card on a confirmed, unpaid booking
// -----------------------------------------------------------------------------
export interface GiftCardApplyResult {
  applied?: true;
  error?: string;
  amount_cents?: number;
  remaining_cents?: number;
  fully_paid?: boolean;
  balance_after_cents?: number;
}

export async function applyGiftCardToBooking(bookingId: string, code: string): Promise<GiftCardApplyResult> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_gift_card_apply_to_booking', { p_booking_id: bookingId, p_code: code });
  if (error) throw error;
  return data as GiftCardApplyResult;
}

export async function removeGiftCardFromBooking(bookingId: string) {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_gift_card_remove_from_booking', { p_booking_id: bookingId });
  if (error) throw error;
  return data as { removed?: true; error?: string; amount_cents?: number };
}

export function giftCardApplyErrorText(code: string | undefined): string {
  switch (code) {
    case 'gift_card_not_found':     return 'Deze cadeaubon bestaat niet.';
    case 'gift_card_not_activated': return 'Deze cadeaubon is nog niet geactiveerd (eerst opwaarderen).';
    case 'gift_card_blocked':       return 'Deze cadeaubon is geblokkeerd.';
    case 'gift_card_expired':       return 'Deze cadeaubon is verlopen. Waarde toevoegen maakt hem weer geldig.';
    case 'gift_card_empty':         return 'Er staat geen saldo op deze cadeaubon.';
    case 'gift_card_already_applied': return 'Er is al een cadeaubon op deze boeking gebruikt.';
    case 'partly_paid':             return 'Er is al (deels) betaald; een cadeaubon kan nu niet meer.';
    case 'not_payable':             return 'Deze boeking staat niet open voor betaling.';
    case 'invalid_lifecycle':       return 'Deze boeking kan in deze status niet afgerekend worden.';
    case 'nothing_due':             return 'Er staat niets open.';
    case 'already_paid':            return 'De boeking is al betaald; annuleer met terugbetaling om de cadeaubon terug te zetten.';
    default:                        return code ?? 'Onbekende fout.';
  }
}

// -----------------------------------------------------------------------------
// Totals for the books (fn_admin_gift_card_liability)
// -----------------------------------------------------------------------------
export interface GiftCardLiability {
  cards_total: number;
  cards_active: number;
  cards_inactive: number;
  outstanding_cents: number;
  expired_cents: number;
  blocked_cents: number;
  held_cents: number;
  loaded_cents: number;
  spent_cents: number;
  credited_back_cents: number;
  as_of: string;
}

export async function fetchGiftCardLiability(): Promise<GiftCardLiability> {
  const { data, error } = await supabase.schema('booking').rpc('fn_admin_gift_card_liability');
  if (error) throw error;
  return data as GiftCardLiability;
}
